import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { useAuth } from '../auth/AuthContext';
import { useFeatureFlag } from '../flags/feature-flags';
import { useTheme } from '../theme/useTheme';
import { CommandPalette } from '../components/CommandPalette';
import {
  ADMIN_MODULES,
  PLATFORM_MODULES,
  PROTOTYPE_MODULES,
  findByPath,
  flattenModules,
  liveItems,
  type NavModule,
} from '../navigation/modules';

/**
 * Shell do Nexora — rail 280px + topbar com breadcrumb.
 *
 * Segue o design system "Core Enterprise Logic" (base-html/core_enterprise_logic_2),
 * cujos tokens já estavam no `tailwind.config.js`. O shell anterior era dark
 * glassmorphism (rail 240px, header translúcido, ícones unicode, fundo animado) —
 * estética oposta à que a spec pede ("professional light-mode", "calm control") e
 * incompatível com a paleta clara das 128 telas de referência.
 */

interface MainLayoutProps {
  children: ReactNode;
}

function initials(name: string | undefined, fallback: string): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  const first = parts[0];
  if (!first) return fallback;
  const last = parts.length > 1 ? parts[parts.length - 1] : undefined;
  if (!last) return first.slice(0, 2).toUpperCase();
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

export function MainLayout({ children }: MainLayoutProps): JSX.Element {
  const { user, tenant, tenants, logout, switchTenant } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggle: toggleTheme } = useTheme();

  const posEnabled = useFeatureFlag('offline-pos');
  const manufacturingEnabled = useFeatureFlag('manufacturing');

  const [railOpen, setRailOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [tenantMenuOpen, setTenantMenuOpen] = useState(false);
  const [openModules, setOpenModules] = useState<Record<string, boolean>>({});
  const tenantRef = useRef<HTMLDivElement>(null);

  const isAdmin = useMemo(
    () =>
      user?.roles?.some((r) => ['admin', 'manager', 'owner'].includes(r.toLowerCase())) ?? false,
    [user],
  );

  /**
   * Gating por feature flag preservado do shell anterior: sem a flag `offline-pos`
   * os itens de PDV somem; sem `manufacturing`, os de produção.
   */
  const modules = useMemo<NavModule[]>(() => {
    const all = [
      ...PROTOTYPE_MODULES,
      ...PLATFORM_MODULES,
      ...(isAdmin ? ADMIN_MODULES : []),
    ];
    return all
      .map((mod) => ({
        ...mod,
        items: mod.items.filter((item) => {
          if (!posEnabled && item.path?.startsWith('/pos')) return false;
          if (!manufacturingEnabled && item.path?.startsWith('/manufacturing')) return false;
          return true;
        }),
      }))
      .filter((mod) => mod.items.length > 0);
  }, [isAdmin, posEnabled, manufacturingEnabled]);

  const flat = useMemo(() => flattenModules(modules), [modules]);
  const active = useMemo(() => findByPath(modules, location.pathname), [modules, location.pathname]);

  // o módulo da rota ativa abre sozinho; o resto preserva a escolha do usuário
  useEffect(() => {
    if (active) setOpenModules((prev) => ({ ...prev, [active.module.id]: true }));
  }, [active]);

  useEffect(() => {
    document.title = active ? `${active.item.label} — Nexora` : 'Nexora ERP';
  }, [active]);

  const go = useCallback(
    (path: string): void => {
      navigate({ to: path }).catch(console.error);
      setRailOpen(false);
    },
    [navigate],
  );

  const step = useCallback(
    (delta: number): void => {
      const i = flat.findIndex((e) => e.path === active?.item.path);
      const next = flat[i + delta];
      if (i >= 0 && next) go(next.path);
    },
    [flat, active, go],
  );

  /* atalhos: Ctrl/Cmd+K abre a paleta, Alt+setas anda na lista linear de telas */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      }
      if (e.altKey && e.key === 'ArrowRight') {
        e.preventDefault();
        step(1);
      } else if (e.altKey && e.key === 'ArrowLeft') {
        e.preventDefault();
        step(-1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [step]);

  /* fecha o seletor de empresa ao clicar fora */
  useEffect(() => {
    if (!tenantMenuOpen) return undefined;
    const onDown = (e: MouseEvent): void => {
      if (!tenantRef.current?.contains(e.target as Node)) setTenantMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [tenantMenuOpen]);

  const handleLogout = async (): Promise<void> => {
    await logout();
    navigate({ to: '/login', replace: true }).catch(console.error);
  };

  const handleSwitchTenant = async (id: string): Promise<void> => {
    setTenantMenuOpen(false);
    await switchTenant(id).catch(console.error);
  };

  const railItemBase =
    'flex w-full items-center gap-sm rounded-lg px-sm py-xs text-left font-body-sm text-body-sm';

  return (
    <div className="flex h-screen overflow-hidden bg-background text-on-surface">
      {/* ───────────────── Global Rail (280px) ───────────────── */}
      <aside
        className={`fixed inset-y-0 left-0 z-[60] flex w-sidebar-width shrink-0 flex-col border-r border-outline-variant bg-surface-container-lowest transition-transform duration-200 lg:static lg:translate-x-0 ${
          railOpen ? 'translate-x-0 shadow-[8px_0_30px_rgba(0,16,36,0.25)]' : '-translate-x-full'
        }`}
      >
        {/* marca */}
        <div className="flex items-center gap-sm px-md pb-sm pt-md">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary font-label-md text-label-md font-bold text-on-primary">
            N
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-headline-sm text-headline-sm font-bold text-primary dark:text-on-surface">
              Nexora
            </h1>
            <p className="font-label-sm text-label-sm text-on-surface-variant">Business OS</p>
          </div>
          <button
            type="button"
            onClick={() => setRailOpen(false)}
            aria-label="Fechar menu"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container lg:hidden"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* seletor multi-empresa — antes era um botão sem ação */}
        <div ref={tenantRef} className="relative mx-sm mb-sm">
          <button
            type="button"
            onClick={() => setTenantMenuOpen((o) => !o)}
            aria-haspopup="listbox"
            aria-expanded={tenantMenuOpen}
            className="flex w-full items-center gap-sm rounded-lg border border-outline-variant bg-surface-container-low px-sm py-xs text-left hover:bg-surface-container"
          >
            <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg bg-primary-container font-label-sm text-label-sm font-bold text-white">
              {initials(tenant?.name, 'NX')}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-label-md text-label-md font-bold text-primary dark:text-on-surface">
                {tenant?.name ?? 'Selecionar empresa'}
              </span>
              {tenant?.slug ? (
                <span className="block truncate font-label-sm text-label-sm text-on-surface-variant">
                  {tenant.slug}
                </span>
              ) : null}
            </span>
            <span className="material-symbols-outlined text-[18px] text-on-surface-variant">
              unfold_more
            </span>
          </button>

          {tenantMenuOpen && tenants.length > 0 ? (
            <ul
              role="listbox"
              className="absolute left-0 right-0 top-full z-10 mt-xs max-h-64 overflow-auto rounded-lg border border-outline-variant bg-surface-container-lowest py-xs shadow-[0_8px_24px_rgba(0,16,36,0.18)]"
            >
              {tenants.map((t) => (
                <li key={t.id} role="option" aria-selected={t.id === tenant?.id}>
                  <button
                    type="button"
                    onClick={() => void handleSwitchTenant(t.id)}
                    className={`flex w-full flex-col px-sm py-xs text-left hover:bg-surface-container ${
                      t.id === tenant?.id ? 'bg-surface-container' : ''
                    }`}
                  >
                    <span className="font-body-sm text-body-sm text-on-surface">{t.name}</span>
                    <span className="font-label-sm text-label-sm text-on-surface-variant">
                      {t.slug}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {/* navegação */}
        <nav aria-label="Navegação principal" className="flex-1 overflow-y-auto px-sm pb-md">
          {modules.map((mod) => {
            const live = liveItems(mod);
            const isOpen = openModules[mod.id] ?? false;
            const isActiveModule = active?.module.id === mod.id;
            return (
              <div key={mod.id} className="mb-[2px]">
                <button
                  type="button"
                  onClick={() => setOpenModules((p) => ({ ...p, [mod.id]: !isOpen }))}
                  aria-expanded={isOpen}
                  className={`flex w-full items-center gap-sm rounded-lg px-sm py-xs text-left font-label-md text-label-md font-semibold hover:bg-surface-container ${
                    isActiveModule ? 'text-primary dark:text-inverse-primary' : 'text-on-surface-variant'
                  }`}
                >
                  <span className="material-symbols-outlined shrink-0 text-[20px]">{mod.icon}</span>
                  <span className="min-w-0 flex-1 truncate">{mod.label}</span>
                  <span className="shrink-0 rounded-pill bg-surface-container px-xs font-label-sm text-label-sm text-on-surface-variant">
                    {live.length}
                  </span>
                  <span
                    className={`material-symbols-outlined shrink-0 text-[18px] transition-transform ${
                      isOpen ? 'rotate-180' : ''
                    }`}
                  >
                    expand_more
                  </span>
                </button>

                {isOpen ? (
                  <div className="ml-sm border-l border-outline-variant pb-xs pl-sm pt-[2px]">
                    {mod.items.map((item) => {
                      if (item.planned || !item.path) {
                        return (
                          <span
                            key={item.label}
                            aria-disabled="true"
                            title="Tela prevista no protótipo, ainda não implementada"
                            className={`${railItemBase} cursor-not-allowed text-outline`}
                          >
                            <span className="min-w-0 flex-1 truncate">{item.label}</span>
                            <span className="shrink-0 rounded-pill border border-outline-variant px-xs font-label-sm text-label-sm">
                              em breve
                            </span>
                          </span>
                        );
                      }
                      const itemActive = active?.item.path === item.path;
                      const tabCount = item.tabs?.length ?? 0;
                      return (
                        <button
                          key={item.path}
                          type="button"
                          onClick={() => go(item.path as string)}
                          aria-current={itemActive ? 'page' : undefined}
                          className={`${railItemBase} ${
                            itemActive
                              ? 'bg-primary font-semibold text-on-primary'
                              : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface'
                          }`}
                        >
                          <span className="min-w-0 flex-1 truncate">{item.label}</span>
                          {tabCount > 0 ? (
                            <span
                              className={`shrink-0 rounded-pill px-xs font-label-sm text-label-sm ${
                                itemActive
                                  ? 'bg-on-primary/15 text-on-primary'
                                  : 'bg-surface-container text-on-surface-variant'
                              }`}
                            >
                              {tabCount}
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </nav>

        {/* pé do rail */}
        <div className="flex flex-col gap-[2px] border-t border-outline-variant p-sm">
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className={`${railItemBase} text-on-surface-variant hover:bg-surface-container`}
          >
            <span className="material-symbols-outlined text-[18px]">search</span>
            <span className="flex-1">Buscar telas</span>
            <kbd className="rounded border border-b-2 border-outline-variant px-xs font-label-sm text-label-sm">
              Ctrl K
            </kbd>
          </button>
          <button
            type="button"
            onClick={toggleTheme}
            className={`${railItemBase} text-on-surface-variant hover:bg-surface-container`}
          >
            <span className="material-symbols-outlined text-[18px]">
              {theme === 'dark' ? 'light_mode' : 'dark_mode'}
            </span>
            <span className="flex-1">{theme === 'dark' ? 'Tema claro' : 'Tema escuro'}</span>
          </button>
          <button
            type="button"
            onClick={() => void handleLogout()}
            className={`${railItemBase} text-on-surface-variant hover:bg-surface-container`}
          >
            <span className="material-symbols-outlined text-[18px]">logout</span>
            <span className="flex-1">Sair</span>
          </button>
        </div>
      </aside>

      {railOpen ? (
        <button
          type="button"
          aria-label="Fechar menu"
          onClick={() => setRailOpen(false)}
          className="fixed inset-0 z-[55] cursor-default bg-[rgba(0,16,36,0.45)] lg:hidden"
        />
      ) : null}

      {/* ───────────────── Workspace ───────────────── */}
      <div className="relative flex min-w-0 flex-1 flex-col">
        <header className="z-50 flex h-16 shrink-0 items-center gap-sm border-b border-outline-variant bg-surface-container-lowest px-md">
          <button
            type="button"
            onClick={() => setRailOpen(true)}
            aria-label="Abrir menu"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container lg:hidden"
          >
            <span className="material-symbols-outlined">menu</span>
          </button>

          {/* breadcrumb: módulo › tela */}
          <nav aria-label="Trilha de navegação" className="flex min-w-0 items-center gap-xs">
            {active ? (
              <>
                <button
                  type="button"
                  onClick={() =>
                    setOpenModules((p) => ({ ...p, [active.module.id]: true }))
                  }
                  className="hidden whitespace-nowrap font-body-sm text-body-sm text-on-surface-variant hover:text-on-surface hover:underline md:inline"
                >
                  {active.module.label}
                </button>
                <span className="material-symbols-outlined hidden text-[16px] text-outline md:inline">
                  chevron_right
                </span>
                <span className="truncate font-body-sm text-body-sm font-bold text-on-surface">
                  {active.item.label}
                </span>
              </>
            ) : (
              <span className="font-body-sm text-body-sm text-on-surface-variant">Nexora</span>
            )}
          </nav>

          <div className="flex-1" />

          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="flex h-8 items-center gap-sm rounded-lg border border-outline-variant bg-surface-container-low px-sm font-body-sm text-body-sm text-on-surface-variant hover:border-primary md:w-[300px]"
          >
            <span className="material-symbols-outlined text-[18px]">search</span>
            <span className="hidden flex-1 truncate text-left md:block">
              Buscar em todos os módulos…
            </span>
            <kbd className="hidden rounded border border-b-2 border-outline-variant px-xs font-label-sm text-label-sm md:block">
              Ctrl K
            </kbd>
          </button>

          <div className="mx-xs hidden h-[22px] w-px bg-outline-variant md:block" />

          <button
            type="button"
            aria-label="Notificações"
            className="relative flex h-8 w-8 items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container"
          >
            <span className="material-symbols-outlined text-[20px]">notifications</span>
          </button>

          <span
            title={user?.name ?? 'Usuário'}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-container font-label-sm text-label-sm font-bold text-white"
          >
            {initials(user?.name, 'U')}
          </span>
        </header>

        {/*
          `transform: translateZ(0)` cria bloco de contenção: prende os drawers
          `fixed right-0` e os modais `fixed inset-0` das telas dentro da área de
          conteúdo, em vez de deixá-los cobrir o rail.
        */}
        <main
          className="relative min-h-0 flex-1 overflow-auto bg-background"
          style={{ transform: 'translateZ(0)' }}
        >
          {children}
        </main>
      </div>

      <CommandPalette
        open={paletteOpen}
        entries={flat}
        onClose={() => setPaletteOpen(false)}
        onSelect={go}
      />
    </div>
  );
}
